// src/forms/html_element_helpers/error_tracking.js

import { fetch_with_timeout } from '../../helpers';
import merge from 'lodash.merge';

/**
 * Tracks errors by POSTing to the action endpoint with a single data_hub_events error event.
 * 
 * @param {Object} params - Named parameters.
 * @param {string} params.internal_error_message - Internal error message for debugging.
 * @param {string} params.external_error_message - User-facing error message.
 * @param {Object} [params.data_hub_instance=window?.data_hub] - The data_hub instance.
 * @param {Object} [params.user_data={}] - Current user data.
 * @param {Object} [params.metadata={}] - Merged metadata (page + form).
 * @param {Object} [params.custom_error_data={}] - Additional error-specific data.
 * @param {Function} [params.error_function] - Error callback for tracking failures (optional, won't throw).
 * 
 * @returns {Promise<void>} Promise that resolves when error tracking is complete (or fails silently).
 */
export async function track_error_to_action_endpoint({
	internal_error_message,
	external_error_message,
	data_hub_instance = window?.data_hub,
	user_data = {},
	metadata = {},
	custom_error_data = {},
	error_function = null
}) {
	// Don't track if no data_hub_instance or forms_domain
	if (!data_hub_instance || !data_hub_instance.forms_domain) {
		if (error_function) {
			error_function('Error tracking skipped: no data_hub_instance or forms_domain', null);
		}
		return;
	}

	try {
		// Create a single error event for logging
		const error_events = [
			{
				verb: 'data_hub_events',
				data: {
					event_name: 'error',
					source: 'data_hub_form',
					custom_data: {
						...custom_error_data,
						internal_error_message: internal_error_message,
						external_error_message: external_error_message,
						error_timestamp: new Date().toISOString()
					}
				}
			}
		];

		// Merge user_data from data_hub_instance if available
		const merged_user_data = merge(
			{},
			data_hub_instance?.user_data || {},
			window?.data_hub_user_data || {},
			user_data
		);

		// Merge metadata
		const merged_metadata = merge(
			{},
			data_hub_instance?.page_metadata || {},
			metadata
		);

		// Build the action object to send
		const error_action_object = {
			events: error_events,
			returns: {
				user_data: '{{user_data}}',
				metadata: '{{metadata}}'
			},
			user_data: merged_user_data,
			metadata: merged_metadata
		};

		// Add JWT if available
		if (data_hub_instance.jwt_cookie) {
			error_action_object.jwt = data_hub_instance.jwt_cookie;
		}

		// Build the API URL
		const error_tracking_api_url = `${data_hub_instance.forms_domain}/api/post/user_data`;

		// Create headers
		const headers = {
			'Content-Type': 'application/json'
		};

		// POST to action endpoint (fire and forget - don't block on errors)
		await fetch_with_timeout(error_tracking_api_url, {
			method: 'POST',
			headers: headers,
			credentials: 'include',
			body: JSON.stringify(error_action_object),
			timeout: 5000
		}).catch(error => {
			// Silently fail - we don't want error tracking to cause additional errors
			if (error_function) {
				error_function('Error tracking POST failed (non-blocking): ' + error, null);
			}
		});

	} catch (error) {
		// Silently fail - we don't want error tracking to cause additional errors
		if (error_function) {
			error_function('Error in track_error_to_action_endpoint (non-blocking): ' + error, null);
		}
	}
}

